import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  $accountCardListStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
  $transferRequestsUpdateStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils } from '@shared/utils';
import { type AccountResponse, AccountType, AmountTypeEnum, TransferRecipientTypeEnum, TransferRequestTypeEnum } from '@shared/api';
import { FilesFormBlock, FilesFormData, PaymentFormBlock, PaymentFormData, SelectAccount, TransferConfirmationBlock } from '@widgets';
import { Button, ErrorMessage, Spinner } from '@shared/widgets';
import { Constants } from '@shared/constants';

type IFormDataOwnAccount = PaymentFormData & FilesFormData;
const DEFAULT_VALUES: IFormDataOwnAccount = {
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  files: [],
};

interface OwnAccountTabProps {
  selectedAccountId?: number;
  accountFrom: AccountResponse | null;
}

export const OwnAccountTab = (props: OwnAccountTabProps) => {
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const accountStore = useUnit($accountCardListStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);
  const transferRequestsUpdateStore = useUnit($transferRequestsUpdateStore.store);

  const [accountTo, setAccountTo] = useState<AccountResponse | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [errorSelect, setErrorSelect] = useState<string>('');

  const formId = useId();
  const formControl = useForm<IFormDataOwnAccount>({ defaultValues: DEFAULT_VALUES });
  const amount = formControl.watch('amount');
  const beneficiaryAmount = formControl.watch('beneficiaryAmount');

  const { t } = useTranslation('payments');

  useEffect(() => {
    $accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment });

    return () => {
      $accountCardListStore.events.clear();
      $transferRequestsUpdateStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  const requestFeeHandler = useCallback(() => {
    if (!amount || !accountTo) return;
    $transferRequestsFeeStore.events.getFee({
      AccountId: props.accountFrom?.id,
      SenderAmount: amount,
      FastPayment: true,
      BeneficiaryCurrencyCode: accountTo?.currency,
      BeneficiaryBankCountryCode: 'USA',
      TransferRequestType: TransferRequestTypeEnum.BetweenInsigniaAccount,
    });
  }, [accountTo?.currency, amount, props.accountFrom?.id]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [props.accountFrom, accountTo]);

  useEffect(() => {
    if (props.accountFrom?.currency && accountTo?.currency && props.accountFrom?.id) {
      if (props.accountFrom.currency === accountTo.currency) {
        $transferRequestsGetFxRateStore.events.setRate(1);
        return;
      }
      $transferRequestsGetFxRateStore.events.getFxRate({
        AccountId: props.accountFrom?.id,
        FromCurrency: props.accountFrom?.currency,
        ToCurrency: accountTo?.currency,
        CountryCode: 'DE',
      });
    }
  }, [accountTo?.currency, props.accountFrom?.currency]);

  const onSubmit = async (data: IFormDataOwnAccount) => {
    if (!accountTo) setErrorSelect('form.errorMessages.requiredAccount');
    if (!props.accountFrom || !accountTo) return;

    const isValid = await formControl.trigger();
    if (!isValid) return;

    if (transferRequestsCreateStore?.data) {
      await $transferRequestsUpdateStore.events.updateTransfer({
        id: transferRequestsCreateStore.data.id,
        type: TransferRequestTypeEnum.BetweenInsigniaAccount,
        fromAccountId: props.selectedAccountId,
        beneficiaryCurrency: accountTo.currency,
        beneficiaryAccountId: accountTo.id,
        purpose: data.purpose,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        beneficiaryCountryCode: 'USA',
        fastPayment: true,
        StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        formControl.setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const newTransferRequest = await $transferRequestsCreateStore.events.createTransfer({
      type: TransferRequestTypeEnum.BetweenInsigniaAccount,
      fromAccountId: props.accountFrom.id,
      beneficiaryCurrency: accountTo.currency,
      beneficiaryAccountId: accountTo.id,
      purpose: data.purpose,
      senderAmount: data.amount,
      totalAmount: data.amount,
      beneficiaryAmount: data.beneficiaryAmount,
      reasonOfThePayment: data.paymentReason,
      fastPayment: true,
      StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    if (newTransferRequest.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: newTransferRequest.data.id, files: data.files });
      formControl.setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = Constants.BANK_COUNTRY_CODE;
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Business));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data]);
  return (
    <FormProvider {...formControl}>
      {formControl.formState.isSubmitting && <Spinner position />}
      <div className={cn(PaletteUtils.isInsigniaPalette && 'mt-6', PaletteUtils.isVcfcPalette && 'bg-white dark:bg-soft-black')}>
        <div className='bg-white p-6 dark:bg-soft-black'>
          <SelectAccount
            loading={accountStore.loading}
            type={AccountType.Payment}
            accounts={accountStore?.data?.filter((acc) => acc.id !== props.selectedAccountId)}
            currentAccount={accountTo}
            label={t('betweenAccounts.placeholders.transferTo')}
            balanceFieldName='availableBalance'
            onChange={setAccountTo}
          />
          {accountTo ? '' : <ErrorMessage text={errorSelect} />}
        </div>
        <form id={formId} className={cn('mt-6', PaletteUtils.isVcfcPalette && 'mt-0')} noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
          <PaymentFormBlock
            className='bg-white p-6 dark:bg-soft-black'
            account={props.accountFrom}
            accountTo={accountTo}
            currency={props.accountFrom?.currency}
            reasonOptions={reasonOptions}
            onRequestFee={requestFeeHandler}
          />
          <FilesFormBlock
            className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
            transactionId={transferRequestsCreateStore.data?.id}
          />
        </form>
        <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 rounded-b-xl bg-white p-6 pt-0 dark:bg-soft-black')}>
          <Button
            className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
            color='primary'
            type='submit'
            form={formId}
            disabled={
              feeStore.loading || transferRequestsCreateStore.loading || transferRequestsUpdateStore.loading || formControl.formState.isSubmitting
            }
          >
            {t('form.buttons.transfer')}{' '}
            {beneficiaryAmount! > 0 &&
              ` ${AmountUtils.transformToAmountWithCurrency(beneficiaryAmount! + (feeStore.data || 0), accountTo?.currency)}`}
          </Button>
        </div>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          showPaymentModal={showPaymentModal}
          onClosePaymentModal={() => setShowPaymentModal(false)}
          account={props.accountFrom!}
          accountTo={accountTo!}
        />
      )}
    </FormProvider>
  );
};
